import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, IconButton, Dialog, Grid, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert, CircularProgress, MenuItem, Chip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh, Edit, Receipt, ChevronLeft, ChevronRight, AttachFile, Visibility } from '@mui/icons-material';
import { getBuyRMBTransactions, updateBuyRMBTransaction } from '../../../services/api';

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewTransaction, setViewTransaction] = useState(null);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ status: '', notes: '', payment_receipt: null });
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptFileName, setReceiptFileName] = useState('');
  const [viewReceiptFile, setViewReceiptFile] = useState(null);
  const [viewReceiptFileName, setViewReceiptFileName] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [filter, setFilter] = useState({ status: '', wallet_type: '' });
  const totalPages = Math.ceil(total / rowsPerPage) || 1;

  const fetchData = useCallback(async (pageArg, rowsArg, filterArg = filter) => {
    setLoading(true);
    try {
      const params = {
        page: pageArg + 1,
        page_size: rowsArg,
        ...filterArg
      };
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });
      const res = await getBuyRMBTransactions(params);
      const results = res.results || res;
      const totalCount = res.total || results.length;
      setTransactions(Array.isArray(results) ? results : []);
      setTotal(totalCount);
    } catch (e) {
      console.error('Error fetching transactions:', e);
      setSnackbar({ open: true, message: 'Error fetching transactions', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchData(page, rowsPerPage);
  }, [page, rowsPerPage, fetchData]);

  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
    }
  };

  const handleCustomRowsInputChange = (e) => {
    const value = e.target.value;
    if (value === '' || (Number(value) > 0 && Number(value) <= 1000)) {
      setCustomRowsInput(value);
    }
  };

  const handleCustomRowsInputBlur = () => {
    if (customRowsInput && Number(customRowsInput) > 0) {
      setRowsPerPage(Number(customRowsInput));
      setPage(0);
      setCustomRowsInput('');
    }
    setCustomRowsActive(false);
  };

  const handleRefresh = () => { setPage(0); fetchData(0, rowsPerPage); };

  // View handlers
  const handleViewOpen = (transaction) => {
    setViewTransaction(transaction);
    setViewReceiptFile(null);
    setViewReceiptFileName('');
    setViewOpen(true);
  };

  const handleViewClose = () => {
    setViewOpen(false);
    setViewTransaction(null);
    setViewReceiptFile(null);
    setViewReceiptFileName('');
  };

  const handleViewFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      const fileType = file.type || file.name.split('.').pop().toLowerCase();
      const isValidType = allowedTypes.includes(file.type) || 
                          ['jpeg', 'jpg', 'png', 'pdf'].includes(fileType);
      
      if (!isValidType) {
        setSnackbar({ 
          open: true, 
          message: 'Invalid file type. Please upload JPEG, JPG, PNG, or PDF files only.', 
          severity: 'error' 
        });
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setSnackbar({ 
          open: true, 
          message: 'File size too large. Maximum size is 10MB.', 
          severity: 'error' 
        });
        return;
      }
      
      setViewReceiptFile(file);
      setViewReceiptFileName(file.name);
    }
  };

  const handleViewRemoveFile = () => {
    setViewReceiptFile(null);
    setViewReceiptFileName('');
  };

  const handleViewUpdateReceipt = async () => {
    if (!viewReceiptFile) {
      setSnackbar({ 
        open: true, 
        message: 'Please select a file to upload', 
        severity: 'error' 
      });
      return;
    }

    try {
      const updateData = {
        status: viewTransaction.status,
        notes: viewTransaction.notes || '',
        payment_receipt: viewReceiptFile,
      };

      await updateBuyRMBTransaction(viewTransaction.sid, updateData);
      window.dispatchEvent(new Event('rmb-transactions:pending-count-refresh'));
      setSnackbar({ open: true, message: 'Receipt updated successfully', severity: 'success' });
      handleViewClose();
      fetchData(page, rowsPerPage);
    } catch (e) {
      const errorMessage = e.response?.data?.payment_receipt?.[0] || 
                          e.response?.data?.detail || 
                          Object.values(e.response?.data || {}).flat().join(', ') || 
                          'Error updating receipt';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  // Edit handlers
  const handleOpen = (transaction = null) => {
    setEditId(transaction ? transaction.sid : null);
    setForm(transaction ? {
      status: transaction.status || '',
      notes: transaction.notes || '',
      payment_receipt: null,
    } : { status: '', notes: '', payment_receipt: null });
    setReceiptFile(null);
    setReceiptFileName('');
    setOpen(true);
  };
  const handleClose = () => { 
    setOpen(false); 
    setForm({ status: '', notes: '', payment_receipt: null }); 
    setEditId(null);
    setReceiptFile(null);
    setReceiptFileName('');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      const fileType = file.type || file.name.split('.').pop().toLowerCase();
      const isValidType = allowedTypes.includes(file.type) || 
                          ['jpeg', 'jpg', 'png', 'pdf'].includes(fileType);
      
      if (!isValidType) {
        setSnackbar({ 
          open: true, 
          message: 'Invalid file type. Please upload JPEG, JPG, PNG, or PDF files only.', 
          severity: 'error' 
        });
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setSnackbar({ 
          open: true, 
          message: 'File size too large. Maximum size is 10MB.', 
          severity: 'error' 
        });
        return;
      }
      
      setReceiptFile(file);
      setReceiptFileName(file.name);
      setForm({ ...form, payment_receipt: file });
    }
  };

  const handleRemoveFile = () => {
    setReceiptFile(null);
    setReceiptFileName('');
    setForm({ ...form, payment_receipt: null });
  };

  const handleSave = async () => {
    if (!form.status) {
      setSnackbar({ open: true, message: 'Status is required', severity: 'error' });
      return;
    }
    
    // Validate that receipt is required for completed status
    if (form.status === 'completed' && !receiptFile && !form.payment_receipt) {
      setSnackbar({ 
        open: true, 
        message: 'Payment receipt is required when status is set to completed', 
        severity: 'error' 
      });
      return;
    }
    
    try {
      const updateData = {
        status: form.status,
        notes: form.notes || '',
      };
      
      // Include file if selected
      if (receiptFile) {
        updateData.payment_receipt = receiptFile;
      }
      
      await updateBuyRMBTransaction(editId, updateData);
      window.dispatchEvent(new Event('rmb-transactions:pending-count-refresh'));
      setSnackbar({ open: true, message: 'Transaction updated successfully', severity: 'success' });
      handleClose();
      fetchData(page, rowsPerPage);
    } catch (e) {
      const errorMessage = e.response?.data?.payment_receipt?.[0] || 
                          e.response?.data?.detail || 
                          Object.values(e.response?.data || {}).flat().join(', ') || 
                          'Error updating transaction';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const addSerialNumbers = (data, page, rowsPerPage) =>
    data.map((row, idx) => ({
      ...row,
      sn: (page * rowsPerPage) + idx + 1
    }));

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      processing: 'info',
      completed: 'success',
      failed: 'error',
      cancelled: 'default',
    };
    return colors[status] || 'default';
  };

  const columns = [
    {
      field: 'sn',
      headerName: 'S/N',
      width: 60,
      sortable: false,
    },
    {
      field: 'transaction_id',
      headerName: 'Transaction ID',
      width: 180,
    },
    {
      field: 'user',
      headerName: 'User',
      width: 200,
      renderCell: (params) => {
        if (!params || !params.row) return 'N/A';
        const user = params.row.user;
        if (!user) return 'N/A';
        // Display username, with email as tooltip/subtitle if available
        return (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {user.username || 'N/A'}
            </Typography>
            {user.email && (
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                {user.email}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'wallet_type',
      headerName: 'Wallet Type',
      width: 150,
      renderCell: (params) => {
        const walletType = params.row.wallet_type;
        let label = '';
        let color = 'default';
        if (walletType === 'foreign') {
          label = 'Foreign Alipay';
          color = 'primary';
        } else if (walletType === 'chinese_bank') {
          label = 'Chinese Bank Transfer';
          color = 'success';
        } else {
          label = 'Chinese Alipay';
          color = 'secondary';
        }
        return (
          <Chip
            label={label}
            size="small"
            color={color}
          />
        );
      },
    },
    {
      field: 'rmb_amount',
      headerName: 'RMB Amount',
      width: 120,
      type: 'number',
      renderCell: (params) => {
        if (!params || !params.row) return '0.00 RMB';
        const value = params.row.rmb_amount;
        if (value == null || value === '') return '0.00 RMB';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return '0.00 RMB';
        return `${numValue.toFixed(2)} RMB`;
      },
    },
    {
      field: 'ngn_amount',
      headerName: 'NGN Amount',
      width: 130,
      type: 'number',
      renderCell: (params) => {
        if (!params || !params.row) return '₦0.00';
        const value = params.row.ngn_amount;
        if (value == null || value === '') return '₦0.00';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return '₦0.00';
        return `₦${numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      },
    },
    {
      field: 'exchange_rate',
      headerName: 'Rate',
      width: 100,
      type: 'number',
      renderCell: (params) => {
        if (!params || !params.row) return '₦0.00';
        const value = params.row.exchange_rate;
        if (value == null || value === '') return '₦0.00';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return '₦0.00';
        return `₦${numValue.toFixed(2)}`;
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.status?.charAt(0).toUpperCase() + params.row.status?.slice(1) || 'N/A'}
          size="small"
          color={getStatusColor(params.row.status)}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 180,
      renderCell: (params) => {
        if (!params || !params.row) return 'N/A';
        const value = params.row.created_at;
        if (!value) return 'N/A';
        try {
          const date = new Date(value);
          if (isNaN(date.getTime())) return 'N/A';
          return date.toLocaleString();
        } catch (e) {
          return 'N/A';
        }
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => handleViewOpen(params.row)}
            sx={{ color: '#1976d2' }}
            title="View Details"
          >
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleOpen(params.row)}
            sx={{ color: '#DC143C' }}
            title="Edit"
          >
            <Edit fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Receipt sx={{ color: '#DC143C', fontSize: { xs: '2rem', sm: '2.5rem' } }} />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 'bold',
              color: '#DC143C',
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
            }}
          >
            Buy RMB Transactions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <IconButton
            onClick={handleRefresh}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': { backgroundColor: 'action.hover' }
            }}
          >
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            select
            label="Status"
            value={filter.status}
            onChange={(e) => {
              setFilter({ ...filter, status: e.target.value });
              setPage(0);
            }}
            size="small"
            fullWidth
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="processing">Processing</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            select
            label="Wallet Type"
            value={filter.wallet_type}
            onChange={(e) => {
              setFilter({ ...filter, wallet_type: e.target.value });
              setPage(0);
            }}
            size="small"
            fullWidth
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="foreign">Foreign Alipay</MenuItem>
            <MenuItem value="chinese">Chinese Alipay</MenuItem>
            <MenuItem value="chinese_bank">Chinese Bank Transfer</MenuItem>
          </TextField>
        </Grid>
      </Grid>

      {/* DataGrid */}
      <Box sx={{
        width: '100%',
        overflowX: 'auto',
        minHeight: { xs: 400, sm: 520 },
        '&::-webkit-scrollbar': {
          height: 8,
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(0,0,0,0.1)',
          borderRadius: 4,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.3)',
          borderRadius: 4,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'rgba(0,0,0,0.5)',
        },
      }}>
        <DataGrid
          rows={addSerialNumbers(transactions, page, rowsPerPage)}
          columns={columns}
          getRowId={(row) => row.sid}
          page={page}
          onPageChange={(newPage) => setPage(newPage)}
          pageSize={rowsPerPage}
          onPageSizeChange={(newPageSize) => { setRowsPerPage(newPageSize); setPage(0); }}
          pagination
          rowCount={total}
          paginationMode="server"
          rowsPerPageOptions={[10, 20, 50, 100]}
          disableSelectionOnClick
          loading={loading}
          hideFooter
          sx={{
            border: 'none',
            width: '100%',
            minWidth: 1200, // Ensure minimum width for all columns
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#DC143C',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: 'white',
              fontWeight: 'bold',
              fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }
            },
            '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
              color: 'white'
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#FFF0F3'
            },
            '& .MuiDataGrid-cell': {
              fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
              padding: { xs: '4px 8px', sm: '8px 12px' }
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none'
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none'
            }
          }}
        />
      </Box>

      {/* Custom Pagination Bar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: { xs: 'center', sm: 'space-between' }, 
        mt: 3, 
        gap: { xs: 1, sm: 2 },
        flexWrap: 'wrap',
        flexDirection: { xs: 'column', sm: 'row' }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={handlePrevPage} 
            disabled={page === 0}
            size="small"
            sx={{ 
              '&:disabled': { opacity: 0.5 }
            }}
          >
            <ChevronLeft />
          </IconButton>
          <Typography 
            variant="body2"
            sx={{ 
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              whiteSpace: 'nowrap'
            }}
          >
            Page {totalPages === 0 ? 0 : page + 1} of {totalPages}
          </Typography>
          <IconButton 
            onClick={handleNextPage} 
            disabled={page >= totalPages - 1}
            size="small"
            sx={{ 
              '&:disabled': { opacity: 0.5 }
            }}
          >
            <ChevronRight />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography 
            variant="body2"
            sx={{ 
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              display: { xs: 'none', sm: 'block' }
            }}
          >
            Rows per page:
          </Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ 
                width: { xs: 80, sm: 100 },
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }
              }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ 
                width: { xs: 80, sm: 100 },
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }
              }}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }}
            />
          )}
        </Box>
      </Box>

      {/* Edit Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 1, sm: 2 },
            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Update Transaction Status
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Status"
            value={form.status}
            onChange={e => setForm({ ...form, status: e.target.value })}
            select
            fullWidth
            sx={{ mt: 2 }}
            size="small"
            required
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="processing">Processing</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </TextField>
          <TextField
            label="Notes"
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            fullWidth
            multiline
            rows={4}
            sx={{ mt: 2 }}
            size="small"
            placeholder="Add any notes about this transaction..."
          />
          
          {/* Payment Receipt Upload */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Payment Receipt {form.status === 'completed' && <span style={{ color: '#d32f2f' }}>*</span>}
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
              Upload payment screenshot (JPEG, JPG, PNG, or PDF). {form.status === 'completed' ? 'Required for completed status.' : 'Optional for other statuses.'}
            </Typography>
            
            {!receiptFile ? (
              <Button
                variant="outlined"
                component="label"
                startIcon={<AttachFile />}
                size="small"
                sx={{ mt: 1 }}
              >
                Choose File
                <input
                  type="file"
                  hidden
                  accept=".jpeg,.jpg,.png,.pdf,image/jpeg,image/jpg,image/png,application/pdf"
                  onChange={handleFileChange}
                />
              </Button>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                mt: 1,
                p: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'action.hover'
              }}>
                <AttachFile fontSize="small" />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {receiptFileName}
                </Typography>
                <IconButton size="small" onClick={handleRemoveFile} color="error">
                  <Typography variant="body2">×</Typography>
                </IconButton>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
          <Button
            onClick={handleClose}
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              backgroundColor: '#DC143C',
              '&:hover': { backgroundColor: '#B91C3C' }
            }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog
        open={viewOpen}
        onClose={handleViewClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 1, sm: 2 },
            width: { xs: 'calc(100% - 16px)', sm: 'auto' },
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Transaction Details
        </DialogTitle>
        <DialogContent sx={{ overflowY: 'auto' }}>
          {viewTransaction && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">Transaction ID</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  {viewTransaction.transaction_id}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">Status</Typography>
                <Box sx={{ mb: 2 }}>
                  <Chip 
                    label={viewTransaction.status?.charAt(0).toUpperCase() + viewTransaction.status?.slice(1)} 
                    color={getStatusColor(viewTransaction.status)}
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">User</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {viewTransaction.user?.username || 'N/A'}
                  {viewTransaction.user?.email && (
                    <Typography variant="caption" display="block" color="textSecondary">
                      {viewTransaction.user.email}
                    </Typography>
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">Wallet Type</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {viewTransaction.wallet_type === 'foreign' 
                    ? 'Foreign Alipay Wallet' 
                    : viewTransaction.wallet_type === 'chinese_bank'
                    ? 'Chinese Bank Transfer'
                    : 'Chinese Alipay Wallet'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">RMB Amount</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {parseFloat(viewTransaction.rmb_amount || 0).toFixed(2)} RMB
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">NGN Amount</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  ₦{parseFloat(viewTransaction.ngn_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">Exchange Rate</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  ₦{parseFloat(viewTransaction.exchange_rate || 0).toFixed(2)} per 1 RMB
                </Typography>
              </Grid>
              {/* Recipient Details - Conditional based on transaction type */}
              {(viewTransaction.transaction_type === 'bank' || viewTransaction.wallet_type === 'chinese_bank') ? (
                <>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">Bank Name</Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {viewTransaction.recipient_bank_name || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">Account Number</Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {viewTransaction.recipient_bank_account_number || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">Account Name</Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {viewTransaction.recipient_bank_account_name || 'N/A'}
                    </Typography>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">Recipient Alipay ID</Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {viewTransaction.recipient_alipay_id || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">Recipient Name</Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {viewTransaction.recipient_alipay_name || 'N/A'}
                    </Typography>
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">Created At</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {viewTransaction.created_at ? new Date(viewTransaction.created_at).toLocaleString() : 'N/A'}
                </Typography>
              </Grid>
              {viewTransaction.notes && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Notes</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {viewTransaction.notes}
                  </Typography>
                </Grid>
              )}
              
              {/* Payment Receipt Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Payment Receipt
                </Typography>
                
                {/* Display existing receipt */}
                {viewTransaction.payment_receipt_url && !viewReceiptFile && (
                  <Box sx={{ mb: 2 }}>
                    {viewTransaction.payment_receipt_url.toLowerCase().endsWith('.pdf') ? (
                      <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                          PDF Receipt: <a href={viewTransaction.payment_receipt_url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>View PDF</a>
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ mb: 2 }}>
                        <img 
                          src={viewTransaction.payment_receipt_url} 
                          alt="Payment Receipt" 
                          style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: '8px', border: '1px solid #e0e0e0' }}
                        />
                      </Box>
                    )}
                  </Box>
                )}

                {/* Upload new receipt */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    {viewTransaction.payment_receipt_url 
                      ? 'Upload a new receipt to replace the existing one' 
                      : 'Upload payment receipt'}
                  </Typography>
                  
                  {!viewReceiptFile ? (
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<AttachFile />}
                      size="small"
                    >
                      Choose File
                      <input
                        type="file"
                        hidden
                        accept=".jpeg,.jpg,.png,.pdf,image/jpeg,image/jpg,image/png,application/pdf"
                        onChange={handleViewFileChange}
                      />
                    </Button>
                  ) : (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1, 
                      mt: 1,
                      p: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      backgroundColor: 'action.hover'
                    }}>
                      <AttachFile fontSize="small" />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {viewReceiptFileName}
                      </Typography>
                      <IconButton size="small" onClick={handleViewRemoveFile} color="error">
                        <Typography variant="body2">×</Typography>
                      </IconButton>
                    </Box>
                  )}
                  
                  {viewReceiptFile && (
                    <Button
                      variant="contained"
                      onClick={handleViewUpdateReceipt}
                      sx={{ mt: 2, backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
                      size="small"
                    >
                      Update Receipt
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
          <Button
            onClick={handleViewClose}
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Transactions;
